import { useFragment } from '@apollo/client';
import { Avatar } from '@synoptic/ui-kit/avatar/avatar.tsx';
import { useIsDesktop } from '@synoptic/ui-kit/responsive/hooks.js';
import { FC } from 'react';
import { S3ObjectInput } from '@/__generated__/graphql.ts';
import { gql } from '@/__generated__/index.ts';
import { getUserS3Url } from '@/components/s3/user/get-user-s3-url.ts';
import { avatarBox } from '@/components/update-user-image/update-image-modal.css.ts';
import { UpdateImageModal } from '@/components/update-user-image/update-image-modal.tsx';
import { useUpdateAvatar } from '@/components/update-user-image/use-update-avatar.ts';
import { XComTag } from '@/components/xcom-tag/xcom-tag.tsx';
import { useIsMe } from '../../use-is-me.ts';
import { editableImage } from '../profile-card.css.ts';
import { Auditory } from './auditory.tsx';
import { Identity } from './identity/identity.tsx';
import {
  avatar,
  bio,
  content,
  leftSide,
  mainStatisticsContainer,
} from './main-statistics.css.ts';

const MainStatisticProfileFragment = gql(`
  fragment MainStatisticProfileFragment on User {
    following
    isHighlighted
    username
    fullName
    avatar {
      key
    }
    backgroundImage {
      key
    }
    bio
    xComHandle
    followingCount
    followersCount
  }
`);

export const MainStatistics: FC<{
  userId: string;
}> = ({ userId }) => {
  const isMe = useIsMe();
  const isDesktop = useIsDesktop();
  const { data: user, complete } = useFragment({
    fragment: MainStatisticProfileFragment,
    fragmentName: 'MainStatisticProfileFragment',
    from: {
      __typename: 'User',
      id: userId,
    },
  });
  const url = getUserS3Url(userId, user.avatar?.key);

  const [updateAvatar] = useUpdateAvatar();
  const onAvatarUpdate = (image: S3ObjectInput) => {
    updateAvatar({ avatar: image });
  };

  if (!complete) return null;

  return (
    <div className={mainStatisticsContainer}>
      <div className={leftSide}>
        {isMe ? (
          <UpdateImageModal
            trigger={
              <div className={avatar}>
                <Avatar
                  className={editableImage}
                  size={isDesktop ? 'large' : 'medium'}
                  imageSrc={url?.toString()}
                  seed={userId}
                />
              </div>
            }
            onUpdate={onAvatarUpdate}
            mainTextEmpty={'Pick a profile picture'}
            subTextEmpty={'Recommended size: 400x400 pixels.'}
            mainText={'Looking good!'}
            subText={
              "You're almost set! Make sure your new avatar looks great before finalizing it."
            }
            editorProps={{
              width: 350,
              height: 350,
            }}
            className={avatarBox}
            renderPreview={(previewSrc) => (
              <Avatar
                size={'xl'}
                imageSrc={previewSrc || url?.toString()}
                seed={userId}
              />
            )}
            previewBorderRadius={9999}
          />
        ) : (
          <Avatar
            className={avatar}
            size={isDesktop ? 'large' : 'medium'}
            imageSrc={url?.toString()}
            seed={userId}
          />
        )}
        <Identity
          userId={userId}
          name={user.fullName}
          username={user.username}
          following={user.following}
          isHighlighted={user.isHighlighted ?? false}
          backgroundImageKey={user.backgroundImage?.key}
        />
        <div className={content}>
          <Auditory
            followersCount={user.followersCount}
            followingCount={user.followingCount}
          />
          {user.bio ? <div className={bio}>{user.bio}</div> : null}
          <XComTag xComHandle={user.xComHandle} isMe={isMe} />
        </div>
      </div>
    </div>
  );
};
